// The Live API speaks raw PCM: mic in is 16-bit @ 16 kHz mono, model out is 16-bit @ 24 kHz mono, both
// base64. These convert between base64 / Int16 / Float32 without Node `Buffer`.

export const ASSISTANT_MIC_SAMPLE_RATE = 16000
export const ASSISTANT_PLAYBACK_SAMPLE_RATE = 24000

const workletModuleUrls = new Map<string, string>()

/** A Blob URL for an AudioWorklet module's source text, created once per source per page. */
export function getWorkletModuleUrl(source: string): string {
  const cachedUrl = workletModuleUrls.get(source)
  if (cachedUrl) return cachedUrl

  const url = URL.createObjectURL(new Blob([source], { type: 'application/javascript' }))
  workletModuleUrls.set(source, url)
  return url
}

const BYTES_PER_SAMPLE = 2
/** Keeps String.fromCharCode under its argument-count limit on large buffers. */
const BASE64_CHUNK_SIZE = 0x8000 // 32768 bytes
const INT16_MAX = 0x7fff // 32767
const INT16_MIN_MAGNITUDE = 0x8000 // 32768

export function int16ToBase64(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength)
  return bytesToBase64(bytes)
}

export function base64ToInt16(base64: string): Int16Array {
  const bytes = base64ToBytes(base64)
  // A truncated frame would make the Int16Array constructor throw, which would kill the message
  // handler mid-turn, so an odd trailing byte is dropped instead.
  const usableLength = bytes.byteLength - (bytes.byteLength % BYTES_PER_SAMPLE)
  // Copy into an aligned buffer so the Int16Array view is valid regardless of byte offset.
  const aligned = new Uint8Array(usableLength)
  aligned.set(bytes.subarray(0, usableLength))
  return new Int16Array(aligned.buffer)
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK_SIZE))
  }
  return btoa(binary)
}

export function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/** Float32 [-1,1] → Int16 PCM. */
export function float32ToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) {
    const clamped = Math.max(-1, Math.min(1, input[i]))
    out[i] = clamped < 0 ? clamped * INT16_MIN_MAGNITUDE : clamped * INT16_MAX
  }
  return out
}

/** Int16 PCM → Float32 [-1,1] (for Web Audio playback). */
export function int16ToFloat32(input: Int16Array): Float32Array {
  const out = new Float32Array(input.length)
  for (let i = 0; i < input.length; i++) out[i] = input[i] / INT16_MIN_MAGNITUDE
  return out
}

/** Speech peaks well below full scale, so RMS is scaled against this rather than 1.0. */
const SPEECH_FULL_SCALE_RMS = 0.15

/** Byte time-domain samples are centred on 128; this converts one to the [-1, 1] range. */
const BYTE_SAMPLE_CENTRE = 128

/**
 * Loudness of whatever an analyser is currently hearing, as 0–1. Reading the analyser rather than the
 * capture callback keeps the level indicator out of the audio path entirely — nothing it does can slow
 * capture down, and it needs no React state to update.
 */
export function measureAnalyserLevel(analyser: AnalyserNode | undefined): number {
  if (!analyser) return 0
  const samples = new Uint8Array(analyser.fftSize)
  analyser.getByteTimeDomainData(samples)

  let sumOfSquares = 0
  for (let i = 0; i < samples.length; i++) {
    const centred = (samples[i] - BYTE_SAMPLE_CENTRE) / BYTE_SAMPLE_CENTRE
    sumOfSquares += centred * centred
  }
  const rms = Math.sqrt(sumOfSquares / samples.length)
  return Math.min(1, rms / SPEECH_FULL_SCALE_RMS)
}

/**
 * Downsample to the target rate by averaging each source window, and a no-op when the rates already
 * match — which is the normal path now that the mic context is created at 16 kHz.
 *
 * Averaging rather than picking one sample per window matters: plain decimation has no anti-aliasing,
 * so every frequency above half the target rate folds back down into the speech band as noise. Only
 * reached where the browser refused the requested context rate.
 */
export function downsampleFloat32(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (toRate >= fromRate) return input
  const ratio = fromRate / toRate
  const outLength = Math.floor(input.length / ratio)
  const out = new Float32Array(outLength)

  for (let i = 0; i < outLength; i++) {
    const start = Math.floor(i * ratio)
    const end = Math.min(input.length, Math.floor((i + 1) * ratio))
    let sum = 0
    for (let j = start; j < end; j++) sum += input[j]
    out[i] = end > start ? sum / (end - start) : 0
  }
  return out
}
